import type { AdminSyncConflict, AdminSyncConflictDetail } from '@/admin/adminApi';
import { causeOf } from '@/admin/synchronization/syncPresentation';
import { pageCount, totalIsKnown, type PageState } from '@/admin/users/accountPresentation';
import { ENTITY_TYPE_LABELS } from '@/admin/users/accountUsagePresentation';
import type { BadgeTone } from '@/ui/kit';

/**
 * Mise en forme du registre des conflits, ecrite **hors des composants**.
 *
 * Chaque valeur que le serveur peut rendre — etat, classification, resolution,
 * origine, genre d'auteur, code de motif — a ici son libelle francais, et
 * `conflictPresentation.test.ts` relit la migration du registre pour echouer des
 * qu'une valeur n'en a pas (comme `auditActionsCoverage.test.ts` pour le
 * journal). **Une valeur inconnue s'affiche telle que le serveur l'a ecrite**,
 * jamais rangee dans « autre » : si le registre en gagne une, l'ecran la montre.
 *
 * Aucun nombre n'est calcule ici : totaux et compteurs sont ceux du serveur.
 */

// ---------------------------------------------------------------------------
// Etats (ADR 0039, point 2)
// ---------------------------------------------------------------------------

export const STATUS_LABELS: Readonly<Record<string, string>> = {
  ouvert: 'Ouvert',
  renvoye_utilisatrice: 'Renvoyé à l’utilisatrice',
  resolu_appareil: 'Résolu sur l’appareil',
  resolu_admin: 'Résolu par l’administration',
  classe: 'Classé',
  sans_objet: 'Sans objet',
};

/** L'etat est toujours ecrit : le ton double le libelle, il ne le remplace pas. */
export const STATUS_TONES: Readonly<Record<string, BadgeTone>> = {
  ouvert: 'warning',
  renvoye_utilisatrice: 'info',
  resolu_appareil: 'success',
  resolu_admin: 'success',
  classe: 'neutral',
  sans_objet: 'neutral',
};

export const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

export const statusTone = (status: string): BadgeTone => STATUS_TONES[status] ?? 'neutral';

export const STATUS_OPTIONS: readonly { readonly value: string; readonly label: string }[] = Object.entries(STATUS_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Comment le conflit a ete tranche, quand il l'a ete (`resolution` n'existe que pour « résolu sur l'appareil »). */
export const RESOLUTION_LABELS: Readonly<Record<string, string>> = {
  garde_sa_version: 'L’utilisatrice a gardé sa version',
  garde_le_foyer: 'L’utilisatrice a gardé la version du foyer',
  meme_contenu_accepte: 'Le même contenu a été accepté ensuite',
  supplante_sans_preuve: 'Remplacé par une saisie ultérieure de l’appareil, sans preuve de son intention',
};

export const resolutionLabel = (resolution: string): string => RESOLUTION_LABELS[resolution] ?? resolution;

/**
 * Ce que dit l'etat d'une resolution : la resolution de l'appareil quand il y en
 * a une, la decision serveur de l'administration sinon. `null` pour un conflit
 * qui n'a pas ete tranche.
 */
export const outcomeLabel = (conflict: Pick<AdminSyncConflict, 'status' | 'resolution'>): string | null => {
  if (conflict.resolution !== null) {
    return resolutionLabel(conflict.resolution);
  }

  return conflict.status === 'resolu_admin' ? 'La version du serveur est gardée' : null;
};

/**
 * Un renvoi a l'utilisatrice dont l'echeance est passee est **sans reponse**
 * (ADR 0039, point 2) : rien ne l'a clos, il attend toujours. `now` est lu au
 * rendu, comme tout affichage de duree ecoulee de l'ecran.
 */
export const isReferralOverdue = (
  conflict: Pick<AdminSyncConflictDetail, 'status' | 'referralExpiresAt'>,
  now: Date,
): boolean =>
  conflict.status === 'renvoye_utilisatrice' &&
  conflict.referralExpiresAt !== null &&
  new Date(conflict.referralExpiresAt).getTime() < now.getTime();

/**
 * Execution d'une decision de l'administration : tant que l'appareil ne l'a pas
 * acquittee, « resolu » ne dit pas que sa file a ete touchee (ADR 0039, point 2).
 * `null` pour tout autre etat.
 */
export const executionLabel = (
  conflict: Pick<AdminSyncConflictDetail, 'status' | 'decisionClaimedAt' | 'deviceAckAt'>,
): string | null => {
  if (conflict.status !== 'resolu_admin') {
    return null;
  }

  if (conflict.deviceAckAt !== null) {
    return 'Exécutée : l’appareil l’a acquittée';
  }

  return conflict.decisionClaimedAt === null
    ? 'Exécution en attente : l’appareil n’a pas encore pris la décision en charge'
    : 'Prise en charge par l’appareil, acquittement en attente';
};

// ---------------------------------------------------------------------------
// Classification (ADR 0039, point 3)
// ---------------------------------------------------------------------------

export interface ClassificationText {
  readonly label: string;
  /** Ce que la classification dit au support : un defaut du client, un vrai conflit, ou rien de sur. */
  readonly nature: string;
  readonly tone: BadgeTone;
  readonly explanation: string;
}

export const CLASSIFICATIONS: Readonly<Record<string, ClassificationText>> = {
  concurrent: {
    label: 'Concurrent',
    nature: 'Vrai conflit',
    tone: 'warning',
    explanation: 'Une version intermédiaire a été écrite par un autre compte, ou par un autre appareil connu.',
  },
  base_nulle: {
    label: 'Base absente',
    nature: 'Défaut probable du client',
    tone: 'neutral',
    explanation:
      'L’appareil a modifié une entité que le serveur connaît sans indiquer la version dont il partait.',
  },
  base_en_avance: {
    label: 'Base en avance',
    nature: 'Défaut probable du client',
    tone: 'neutral',
    explanation: 'L’appareil croyait connaître une version plus récente que celle du serveur.',
  },
  rattrapage_propre: {
    label: 'Rattrapage de l’appareil',
    nature: 'Défaut probable du client',
    tone: 'neutral',
    explanation: 'Toutes les versions intermédiaires viennent du même appareil : il s’est devancé lui-même.',
  },
  inconnue: {
    label: 'À examiner',
    nature: 'Indéterminé',
    tone: 'info',
    explanation: 'Le journal ne permet pas de trancher, par exemple une version intermédiaire sans appareil connu.',
  },
};

export const classificationText = (classification: string): ClassificationText =>
  CLASSIFICATIONS[classification] ?? { label: classification, nature: 'Indéterminé', tone: 'neutral', explanation: '' };

export const CLASSIFICATION_OPTIONS: readonly { readonly value: string; readonly label: string }[] = Object.entries(
  CLASSIFICATIONS,
).map(([value, text]) => ({ value, label: `${text.label} · ${text.nature.toLocaleLowerCase('fr-FR')}` }));

// ---------------------------------------------------------------------------
// Origine, auteur, motif, operation
// ---------------------------------------------------------------------------

export const ORIGIN_LABELS: Readonly<Record<string, string>> = {
  push: 'Envoi d’un appareil',
  reprise: 'Reprise de l’historique',
};

export const originLabel = (origin: string): string => ORIGIN_LABELS[origin] ?? origin;

export const ACTOR_KIND_LABELS: Readonly<Record<string, string>> = {
  appareil: 'Appareil',
  admin: 'Administration',
  systeme: 'Système',
};

export const actorKindLabel = (kind: string): string => ACTOR_KIND_LABELS[kind] ?? kind;

/**
 * Codes de motif d'une transition : une liste fermee, jamais un texte saisi
 * (donnees de sante). Les codes viennent de deux enumerations du serveur,
 * `ConflictTransitionReason` et `NoLongerRelevantProof` (les preuves d'un conflit
 * sans objet) : `conflictPresentation.test.ts` les relit et echoue des qu'un code
 * n'a pas de libelle ici. Y figurent des maintenant les motifs de classement de
 * l'ADR 0039 que les actions de #636 ecriront.
 */
export const REASON_CODE_LABELS: Readonly<Record<string, string>> = {
  detection: 'Conflit détecté',
  reprise: 'Conflit repris de l’historique',
  preuve_meme_cle_meme_empreinte: 'Preuve : même clé et même contenu acceptés ensuite',
  preuve_meme_empreinte_acceptee: 'Preuve : même contenu accepté ensuite',
  preuve_entite_supprimee: 'Preuve : enregistrement supprimé ensuite',
  meme_contenu_accepte: 'Le même contenu a été accepté',
  garde_sa_version: 'L’utilisatrice a gardé sa version',
  garde_le_foyer: 'L’utilisatrice a gardé la version du foyer',
  purge_version_refusee: 'Version refusée purgée',
  effacement_compte: 'Version refusée effacée avec le compte',
  doublon: 'Doublon d’un autre conflit',
  defaut_client: 'Défaut de l’application',
  version_indisponible: 'Version refusée indisponible',
  appareil_sans_saisie: 'L’appareil n’a plus la saisie',
  obsolete: 'Conflit obsolète',
};

export const reasonCodeLabel = (code: string): string => REASON_CODE_LABELS[code] ?? code;

const OPERATION_LABELS: Readonly<Record<string, string>> = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
};

export const operationLabel = (operationType: string): string => OPERATION_LABELS[operationType] ?? operationType;

/** Motif de refus rendu au push : la cause lue par la supervision, sans son conseil. */
export const refusalLabel = (reason: string): string => causeOf(reason)[0];

// ---------------------------------------------------------------------------
// Natures d'enregistrement
// ---------------------------------------------------------------------------

export const ENTITY_TYPE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = Object.entries(
  ENTITY_TYPE_LABELS,
)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

// ---------------------------------------------------------------------------
// Versions et pagination
// ---------------------------------------------------------------------------

/** « v2 » ou « sans version de départ » : une base absente est dite, pas affichee « 0 ». */
export const baseVersionLabel = (base: number | null): string =>
  base === null ? 'sans version de départ' : `version ${String(base)}`;

export const describeVersions = (conflict: Pick<AdminSyncConflict, 'baseVersion' | 'serverVersion'>): string =>
  `Départ : ${baseVersionLabel(conflict.baseVersion)} · serveur : version ${String(conflict.serverVersion)}`;

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

const conflictCount = (total: number): string => {
  if (total === 0) {
    return 'Aucun conflit';
  }

  return total === 1 ? '1 conflit' : `${NUMBER_FORMAT.format(total)} conflits`;
};

/** Phrase de pagination. Le total est celui du serveur ; quand il manque, l'ecran le dit. */
export const describeConflictPage = (state: PageState): string => {
  if (!totalIsKnown(state.total)) {
    return `Page ${NUMBER_FORMAT.format(state.page)} · ${conflictCount(state.itemCount)} affiché${state.itemCount > 1 ? 's' : ''} · nombre total non communiqué par le serveur`;
  }

  return `${conflictCount(state.total)} · page ${NUMBER_FORMAT.format(state.page)} sur ${NUMBER_FORMAT.format(pageCount(state))}`;
};
